/**
 * Shared conversation-history drawer for the Agent SDK "door" pages (Quality
 * Co-Pilot, Tech Support Co-Pilot, …).
 *
 * A left slide-out listing the logged-in user's OWN threads for that door (the
 * door's `/conversations` list is owner- + profile-scoped server-side). Pick one
 * to resume it, start a new one, or — when `onRename` is provided — rename one
 * inline (the universal editable-title affordance). Presentational: the page owns
 * loading / selection / persistence; this renders props and reports clicks.
 */
import { useEffect, useState } from 'react';

import type { ConversationRead } from '../../services/agentConsole';
import { formatRelativeTime } from './conversationHistory';
import styles from './ConversationHistoryDrawer.module.css';

export interface ConversationHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  conversations: ConversationRead[];
  activeId: string | null;
  loading?: boolean;
  ownerName?: string | null;
  ownerInitials?: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  /** When provided, each item shows a rename (✎) control that calls this with
   *  the trimmed new title (the universal editable-title affordance). */
  onRename?: (id: string, title: string) => void;
  /** Header label (default "Your conversations"). */
  title?: string;
  /** Accessible label for the dialog (defaults to `title`). */
  ariaLabel?: string;
}

export function ConversationHistoryDrawer({
  isOpen,
  onClose,
  conversations,
  activeId,
  loading = false,
  ownerName,
  ownerInitials,
  onSelect,
  onNew,
  onRename,
  title = 'Your conversations',
  ariaLabel,
}: ConversationHistoryDrawerProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  // Escape closes — only while open, so we don't hold a global listener idle.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Closing the drawer abandons any in-progress rename. The reset happens
  // during render (React's documented "adjust state when a prop changes"
  // pattern) rather than in an effect, so the closed drawer never paints a
  // stale edit row for one frame and the react-hooks/set-state-in-effect rule
  // stays clean: https://react.dev/learn/you-might-not-need-an-effect
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) {
      setEditingId(null);
      setDraft('');
    }
  }

  const startRename = (c: ConversationRead) => {
    setEditingId(c.id);
    setDraft(c.title || '');
  };
  const cancelRename = () => {
    setEditingId(null);
    setDraft('');
  };
  const commitRename = (id: string) => {
    const next = draft.trim();
    if (next && onRename) onRename(id, next);
    setEditingId(null);
    setDraft('');
  };

  return (
    <>
      <div
        className={`${styles.backdrop} ${isOpen ? styles.show : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`${styles.drawer} ${isOpen ? styles.show : ''}`}
        role="dialog"
        aria-label={ariaLabel ?? title}
        aria-hidden={!isOpen}
      >
        <div className={styles.head}>
          <span className={styles.title}>{title}</span>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close history"
          >
            ✕
          </button>
        </div>

        {(ownerName || ownerInitials) && (
          <div className={styles.scope}>
            <span className={styles.who}>{ownerInitials || '🙂'}</span>
            <span>
              {ownerName ? `${ownerName} · only your threads` : 'Only your threads'}
            </span>
          </div>
        )}

        <button type="button" className={styles.newBtn} onClick={onNew}>
          ✎ New conversation
        </button>

        <div className={styles.list}>
          {loading && conversations.length === 0 && (
            <div className={styles.state} role="status">
              <span className={styles.spinner} /> Loading…
            </div>
          )}

          {!loading && conversations.length === 0 && (
            <div className={styles.empty}>
              <p className={styles.emptyTitle}>No conversations yet</p>
              <p className={styles.emptySub}>
                Start talking and your history shows up here.
              </p>
            </div>
          )}

          {conversations.map((c) =>
            editingId === c.id ? (
              <div key={c.id} className={styles.editRow}>
                <input
                  className={styles.editInput}
                  value={draft}
                  autoFocus
                  maxLength={200}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitRename(c.id);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelRename();
                    }
                  }}
                  onBlur={cancelRename}
                  aria-label="Conversation name"
                />
                <button
                  type="button"
                  className={styles.editSave}
                  // mousedown fires before the input's blur, so the click isn't
                  // swallowed by onBlur-cancel before commit runs.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commitRename(c.id);
                  }}
                  aria-label="Save name"
                >
                  ✓
                </button>
              </div>
            ) : (
              <div key={c.id} className={styles.itemRow}>
                <button
                  type="button"
                  className={`${styles.item} ${c.id === activeId ? styles.itemActive : ''}`}
                  onClick={() => onSelect(c.id)}
                  aria-current={c.id === activeId}
                >
                  <span className={styles.itemTitle}>
                    {c.title || 'Untitled conversation'}
                  </span>
                  <span className={styles.itemMeta}>
                    {formatRelativeTime(c.last_activity_at)}
                    {c.state === 'running' && (
                      <span
                        className={styles.runningBadge}
                        title="Merlin is working on this conversation"
                      >
                        <span className={styles.runningDot} /> running
                      </span>
                    )}
                  </span>
                </button>
                {onRename && (
                  <button
                    type="button"
                    className={styles.renameBtn}
                    onClick={() => startRename(c)}
                    aria-label="Rename conversation"
                    title="Rename"
                  >
                    ✎
                  </button>
                )}
              </div>
            ),
          )}
        </div>
      </aside>
    </>
  );
}
